import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiBlob, apiList, saveBlob } from '../../api/http';
import { Notice, errorMessage, secondaryButton, selectClass } from '../../components/common';
import { t } from '../../i18n';
import type { Exam } from './ExamsTab';

interface ResultRow {
  studentId: string;
  name: string;
  admissionNumber: string;
  className: string;
  sectionName: string | null;
  totalMarks: number;
  maxMarks: number;
  percentage: number;
  grade: string;
  passed: boolean;
  classRank: number;
  incomplete?: boolean;
}

/** Class results of one exam: a live preview until published, then the frozen snapshot. */
export function ResultsTab({ schoolId }: { schoolId: string }) {
  const [examId, setExamId] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const exams = useQuery({
    queryKey: ['exams', schoolId],
    queryFn: () => api<Exam[]>(`/schools/${schoolId}/exams`),
  });
  const results = useQuery({
    queryKey: ['results', schoolId, examId],
    queryFn: async () => {
      const res = await apiList<ResultRow>(`/schools/${schoolId}/exams/${examId}/results`, {
        query: { pageSize: 100 },
      });
      const body = res as unknown as {
        preview: boolean;
        summary: {
          students: number;
          passed: number;
          failed: number;
          averagePercentage: number | null;
        };
      };
      return { rows: res.data, preview: body.preview, summary: body.summary };
    },
    enabled: !!examId,
  });

  const download = async (row: ResultRow) => {
    try {
      saveBlob(
        await apiBlob(`/students/${row.studentId}/results/${examId}/report-card.pdf`),
        `report-card-${row.admissionNumber}.pdf`,
      );
    } catch (e) {
      setMessage(errorMessage(e));
    }
  };

  const published = exams.data?.find((x) => x.id === examId)?.status === 'published';

  return (
    <section aria-label={t('exam.tab.results')} className="space-y-4">
      <div>
        <label htmlFor="results-exam" className="block text-sm font-medium">
          {t('marks.exam')}
        </label>
        <select
          id="results-exam"
          value={examId}
          onChange={(e) => setExamId(e.target.value)}
          className={selectClass}
        >
          <option value="">{t('marks.choose')}</option>
          {exams.data?.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </select>
      </div>
      {!examId && <p className="text-slate-600">{t('results.pick')}</p>}
      {message && <Notice kind="error">{message}</Notice>}
      {results.isError && <Notice kind="error">{errorMessage(results.error)}</Notice>}
      {results.data?.preview && <Notice kind="error">{t('results.preview')}</Notice>}
      {results.data && (
        <p className="text-sm text-slate-700">
          {t('results.summary', {
            students: results.data.summary.students,
            passed: results.data.summary.passed,
            failed: results.data.summary.failed,
            average: results.data.summary.averagePercentage ?? '—',
          })}
        </p>
      )}
      {results.data?.rows.length === 0 && <p className="text-slate-600">{t('results.empty')}</p>}
      {results.data && results.data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th scope="col" className="p-2">
                  {t('results.rank')}
                </th>
                <th scope="col" className="p-2">
                  {t('results.student')}
                </th>
                <th scope="col" className="p-2">
                  {t('results.class')}
                </th>
                <th scope="col" className="p-2">
                  {t('results.total')}
                </th>
                <th scope="col" className="p-2">
                  {t('results.percentage')}
                </th>
                <th scope="col" className="p-2">
                  {t('results.grade')}
                </th>
                <th scope="col" className="p-2">
                  {t('results.result')}
                </th>
                <th scope="col" className="p-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.data.rows.map((r) => (
                <tr key={r.studentId}>
                  <td className="p-2">{r.classRank}</td>
                  <th scope="row" className="p-2 font-medium">
                    {r.name}
                    {r.incomplete && (
                      <span className="ml-2 text-xs text-amber-800">
                        ({t('results.incomplete')})
                      </span>
                    )}
                  </th>
                  <td className="p-2">
                    {r.className}
                    {r.sectionName ? ` – ${r.sectionName}` : ''}
                  </td>
                  <td className="p-2">
                    {r.totalMarks} / {r.maxMarks}
                  </td>
                  <td className="p-2">{r.percentage}</td>
                  <td className="p-2">{r.grade}</td>
                  <td className="p-2">{r.passed ? t('results.pass') : t('results.fail')}</td>
                  <td className="p-2 text-right">
                    {published && (
                      <button
                        type="button"
                        className={secondaryButton}
                        aria-label={t('results.pdfFor', { name: r.name })}
                        onClick={() => void download(r)}
                      >
                        {t('results.pdf')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
